import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { useCloudflareStorage } from '@/hooks/useCloudflareStorage';
import { Switch } from '@/components/ui/switch';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { Loader2 } from 'lucide-react';

// Esquema actualizado para coincidir con la base de datos
const formSchema = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  content: z.string().min(1, 'El contenido es requerido'),
  coverImage: z.string().min(1, 'La imagen de portada es requerida'),
  category: z.string().min(1, 'La categoría es requerida'),
  isPremium: z.boolean().default(false),
});

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateArticleDialog({ open, onOpenChange }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { createMaterial } = useDigitalMaterials();
  const { uploadFile } = useCloudflareStorage();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      content: '',
      coverImage: '',
      category: '',
      isPremium: false,
    },
    mode: 'onChange',
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      console.log('Form values:', values);

      if (!values.title || !values.description || !values.content || !values.coverImage || !values.category) {
        console.log('Missing required fields:', {
          title: !values.title,
          description: !values.description,
          content: !values.content,
          coverImage: !values.coverImage,
          category: !values.category
        });
        toast({
          title: 'Error de validación',
          description: 'Por favor, completa todos los campos requeridos',
          variant: 'destructive',
        });
        return;
      }

      const material = {
        title: values.title,
        description: values.description,
        content: values.content,
        coverImage: values.coverImage,
        type: 'ARTÍCULO' as const,
        isPremium: values.isPremium,
        category: values.category,
        tags: [],
        isDownloadable: false,
      };

      console.log('Creating material:', material);
      await createMaterial(material);

      toast({
        title: 'Artículo creado',
        description: 'El artículo se ha creado correctamente',
      });

      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating article:', error);
      toast({
        title: 'Error',
        description: 'No se pudo crear el artículo. Por favor, intenta nuevamente.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear nuevo artículo</DialogTitle>
          <DialogDescription>
            Completa la información para crear un nuevo artículo en la biblioteca.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Título del artículo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Breve descripción del artículo"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Contenido</FormLabel>
                  <FormControl>
                    <RichTextEditor
                      value={field.value}
                      onChange={field.onChange}
                      onUploadImage={async (file: File) => {
                        try {
                          const url = await uploadFile(file, 'IMAGE');
                          return url;
                        } catch (error) {
                          console.error('Error uploading image:', error);
                          toast({
                            title: 'Error',
                            description: 'Error al subir la imagen',
                            variant: 'destructive',
                          });
                          return null;
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="coverImage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Imagen de portada</FormLabel>
                  <FormControl>
                    <div className="space-y-4">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={async (e) => {
                          try {
                            const file = e.target.files?.[0];
                            if (!file) {
                              console.log('No file selected for cover image');
                              return;
                            }

                            console.log('Uploading cover image:', file.name, file.type);
                            const url = await uploadFile(file, 'IMAGE');
                            console.log('Upload result:', url);

                            if (url) {
                              console.log('Setting cover image URL:', url);
                              
                              // Actualizar el campo usando form.setValue para asegurar la actualización
                              form.setValue('coverImage', url, {
                                shouldValidate: true,
                                shouldDirty: true,
                                shouldTouch: true
                              });

                              toast({
                                title: 'Imagen subida',
                                description: 'La imagen de portada se ha subido correctamente.',
                              });
                            } else {
                              console.error('No URL in upload result');
                              toast({
                                title: 'Error',
                                description: 'No se pudo obtener la URL de la imagen',
                                variant: 'destructive',
                              });
                            }
                          } catch (error) {
                            console.error('Error uploading cover image:', error);
                            toast({
                              title: 'Error',
                              description: 'Error al subir la imagen de portada',
                              variant: 'destructive',
                            });
                          }
                        }}
                      />
                      {field.value && (
                        <div className="relative w-full aspect-video rounded-lg overflow-hidden border">
                          <img
                            src={field.value}
                            alt="Cover preview"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Error loading cover image preview:', e);
                              toast({
                                title: 'Error',
                                description: 'No se pudo cargar la vista previa de la imagen',
                                variant: 'destructive',
                              });
                            }}
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2 break-all">
                            URL: {field.value}
                          </div>
                        </div>
                      )}
                      <Input
                        type="text"
                        value={field.value || ''}
                        onChange={(e) => {
                          form.setValue('coverImage', e.target.value, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true
                          });
                        }}
                        className="mt-2"
                        placeholder="URL de la imagen de portada"
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    Sube una imagen de portada para el artículo
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría</FormLabel>
                  <FormControl>
                    <Input placeholder="Categoría del artículo" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isPremium"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel>Contenido Premium</FormLabel>
                    <FormDescription>
                      Marca esta opción si el artículo es solo para usuarios premium
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear artículo'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
